import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { API_ROUTES } from '../../_constants/api-route.constant';
import { PagesService } from '../pages/pages.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  private readonly CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
  constructor(
    private http: PagesService) { }

  async uploadFile(fileOrFiles: File | File[]): Promise<string | string[] | undefined> {
    try {
      // If it's a single file, upload it directly
      if (fileOrFiles instanceof File) {
        const response = await firstValueFrom(
          this.http.postFile(`${API_ROUTES.ACCOUNT.uploadImage}`, fileOrFiles)
        );
        return response?.data?.url;
      }

      // If it's an array, upload all files in parallel
      const uploadPromises = fileOrFiles.map(file =>
        firstValueFrom(
          this.http.postFile(`${API_ROUTES.ACCOUNT.uploadImage}`, file)
        ).then(response => response?.data?.url)
          .catch(() => undefined)
      );

      const results = await Promise.all(uploadPromises);
      return results.filter((url): url is string => !!url);
    } catch (err) {
      console.error('Upload failed:', err);
      return undefined;
    }
  }

  initUpload(file: File) {
    return firstValueFrom(
      this.http.post({
        filename: file.name,
        fileSize: file.size,
        contentType: file.type
      }, `${API_ROUTES.UPLOAD.init}`)
    );
  }

  // Step 2: Upload a single chunk
  uploadChunk(uploadId: string, partNumber: number, chunk: Blob) {
    const formData = new FormData();
    formData.append("chunk", chunk);
    formData.append("uploadId", uploadId);
    formData.append("partNumber", String(partNumber));
    return firstValueFrom(
      this.http.post(formData, `${API_ROUTES.UPLOAD.uploadPart}`)
    );
  }

  // Step 3: Complete the upload
  completeUpload(uploadId: string, parts: { ETag: string; PartNumber: number }[]) {
    return firstValueFrom(
      this.http.post({ uploadId, parts }, `${API_ROUTES.UPLOAD.complete}`)
    );
  }

  // Step 4: Main method
  async uploadLargeFile(
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<string> {
    const initData = await this.initUpload(file);
    const uploadId = initData.data.uploadId;
    const parts: { ETag: string; PartNumber: number }[] = [];

    let partNumber = 1;
    let uploadedBytes = 0;

    for (let start = 0; start < file.size; start += this.CHUNK_SIZE) {
      const chunk = file.slice(start, start + this.CHUNK_SIZE);

      const uploadRes = await this.uploadChunk(uploadId, partNumber, chunk);
      parts.push({ ETag: uploadRes.ETag, PartNumber: partNumber });

      uploadedBytes += chunk.size;
      const percent = Math.round((uploadedBytes / file.size) * 100);
      if (onProgress) onProgress(percent);

      partNumber++;
    }

    const completeRes = await this.completeUpload(uploadId, parts);
    return completeRes.data.location; // Final S3 URL
  }
}
